import { SpotFilterBaseIcon } from '@tableau/icons/SpotFilterBaseIcon';
import { SiteUrls } from '@tableau/shared-urls';
import { ButtonWidget } from '@tableau/widgets';
import { BodyCell } from '@tableau/widgets-datagrid';
import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';
import { FieldDataType, FieldOrderField } from '../../../../../gen-src/queries';
import {
  classes,
  ColumnFactory,
  createIconCellFormatter,
  createLabelColumnFormatter,
  createSelectionColumn,
  createSortableHeaderFormatter,
  createTextCellFormatter,
  cssClass,
  ExplorerStyledDisplayGrid,
  FeatureFlagsContext,
  GraphLabel,
  GraphQLError,
  GridSortProps,
  LabelContent,
  reportGraphQLErrors,
  SelectionProviderChildProps,
  SiteUrlsContext,
  TypedBodyCellProps,
  TypedColumn,
  UnsortableColumnKey,
  UseRenderLabelsParams,
  UsualDisplayGridProps,
} from '../../../data-catalog-shared';
import { getLabelParamsForGrids, labelCategoryToString, LabelGridWrapperProps } from '../Labels/LabelsHookHelper';
import { createNumberCellFormatter } from '../datagrid/cellFormatters/NumberCellFormatter';
import {
  createQueryPreservingReplacingLinkCellFormatter,
} from '../datagrid/cellFormatters/QueryPreservingLinkCellFormatter';
import { SvgFieldIcon } from '../field/components/SvgFieldIcon';
import { FieldNameMappings } from '../field/functions/FieldNameMapping';
import { FieldIcon } from '../field/types/FieldIcon';
import { DescriptionIconFromRowData, DescriptionInheritedType, DescriptionTextFromRowData, RowDataType } from '../fieldColumn/DescriptionInfo';
import { getNameWithApplicableDataType } from '../sharedDisplayGrid/FieldsDisplayGrid';
import { FieldsValidSortFields } from '../sorting/sortProviders/FieldsGridSortProvider';

export interface DatasourceFieldsDisplayGridRowData {
  readonly datasourceId: string;
  readonly name: string | null;
  readonly dataType: FieldDataType | null;
  readonly id: string;
  readonly description?: string | null;
  readonly descriptionInherited?: (DescriptionInheritedType | null)[] | null;
  readonly fieldIcon: FieldIcon | null;
  readonly sheetCount: number | null;
  readonly labels?: ReadonlyArray<GraphLabel>;
}

type DatasourceFieldsColumnFactory = ColumnFactory<
  GridSortProps<FieldsValidSortFields>,
  DatasourceFieldsDisplayGridRowData,
  FieldsValidSortFields
>;
function columns(siteUrls: SiteUrls): ReadonlyArray<DatasourceFieldsColumnFactory> {
  const columns: ReadonlyArray<DatasourceFieldsColumnFactory> = [
    () => createSelectionColumn(),
    (sortParams) => ({
      name: 'type',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderType(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createIconCellFormatter(
        ({ fieldIcon }) => <SvgFieldIcon fieldIcon={fieldIcon} />,
        ({ fieldIcon }) => fieldIcon ? FieldNameMappings[fieldIcon]() : ''
      ),
      sortable: false,
      resizable: false
    }),
    () => createLabelGridColumnFormatter(siteUrls, 'WARNING'),
    (sortParams) => ({
      name: 'name',
      dataKey: FieldOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createQueryPreservingReplacingLinkCellFormatter<DatasourceFieldsDisplayGridRowData>(
        getNameWithApplicableDataType,
        (urls, { id, datasourceId }) => urls.datasourceLineageFieldPath(datasourceId, id),
      ),
      sortable: true,
    }),
    (sortParams) => ({
      name: 'sheets',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderSheets(),
      headerFormatter: createSortableHeaderFormatter(sortParams, 'right'),
      cellFormatter: createNumberCellFormatter(({ sheetCount }) => sheetCount),
      sortable: false,
    }),
    (sortParams) => ({
      name: 'description',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderDescription(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: descriptionCellFormatter(),
      sortable: false,
      growToFit: true,
    }),
    () => createLabelGridColumnFormatter(siteUrls, 'SENSITIVITY'),
  ];

  return columns.filter(Boolean) as ReadonlyArray<DatasourceFieldsColumnFactory>;
}
export type DatasourceFieldsDisplayGridAdditionalProps = {
  readonly resetFilter?: () => void;
};

export type DatasourceFieldsDisplayGridProps =
  UsualDisplayGridProps<DatasourceFieldsDisplayGridRowData, GraphQLError> &
  SelectionProviderChildProps &
  GridSortProps<FieldsValidSortFields> &
  DatasourceFieldsDisplayGridAdditionalProps;

export const noContentSearchBase = cssClass('noContentSearchBase',
  {
    display: 'flex',
    flexFlow: 'column',
    overflow: 'auto',
    alignSelf: 'center',
    alignItems: 'center',
    flexGrow: 'inherit',
    justifyContent: 'center',
    fontWeight: 'normal'
  }
);

export function DatasourceFieldsDisplayGrid(props: DatasourceFieldsDisplayGridProps): JSX.Element {
  const { customSensitivityLabelsEnabled, searchAndFilterOnLineagePageEnabled } = React.useContext(FeatureFlagsContext);
  const siteUrls = React.useContext(SiteUrlsContext);
  const cols = columns(siteUrls)
    .map(columnFactory => columnFactory(props))
    .filter(column => {
      if (column.name === 'label-sensitivity' && !customSensitivityLabelsEnabled) {
        return false;
      }
      return true;
    });
    if (props.rowCount === 0 && searchAndFilterOnLineagePageEnabled) {
      return (
        <div className={classes(noContentSearchBase)}>
          <SpotFilterBaseIcon size={80} alt={Messages.FilterNoContent()} />
          <div>{Messages.FilterNoContent()}</div>
          <ButtonWidget
            testId={'lineage-reset-filter'}
            onClick={props.resetFilter ? props.resetFilter : () => { return; }}
          >
            <span >{Messages.ResetFilter()}</span>
          </ButtonWidget>
        </div>
      );
    }
  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      selectedKeys={props.selectedKeys}
      updateSelectionForKey={props.updateSelectionForKey}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}

// Returns a cell formatter that displays a description and, if applicable, a description with inheritance info
function descriptionCellFormatter(): React.Factory<BodyCell.Props> & React.Factory<TypedBodyCellProps<DatasourceFieldsDisplayGridRowData>> {
  return createTextCellFormatter(
    descriptionTextFromRowData, // getTextFromRowData
    Messages.NoDescriptionGridPlaceholder(), // placeholder
    () => false, // getDisabledFromRowData (always enabled)
    false, // noPadding
    descriptionIconFromRowData, // getPostIconFromRowData
  );
}

// Call the helper methods with only the needed fields. I can't call the helper methods
// with a broader row-data type because createTextCellFormatter mandates that the interfaces
// of the helper methods accept the same broad type.
function minifyRowData(rowData: DatasourceFieldsDisplayGridRowData): RowDataType {
  return {
    description: rowData.description,
    descriptionInherited: rowData.descriptionInherited,
  };
}

function descriptionTextFromRowData(rowData: DatasourceFieldsDisplayGridRowData) {
  return DescriptionTextFromRowData(minifyRowData(rowData));
}

function descriptionIconFromRowData(rowData: DatasourceFieldsDisplayGridRowData) {
  return DescriptionIconFromRowData(minifyRowData(rowData));
}

function getLabelContentFromRowData(rowData: DatasourceFieldsDisplayGridRowData): LabelContent {
  const contentParams: LabelContent = {
    contentId: rowData.id,
    contentType: null,
    contentName: rowData.name ?? undefined,
    subjectArea: 'Field'
  };
  return contentParams;
}


function createLabelGridColumnFormatter(urls: SiteUrls, category: LabelGridWrapperProps['category']): TypedColumn<DatasourceFieldsDisplayGridRowData, FieldsValidSortFields> {

  function getPropsFromRowData(rowData: DatasourceFieldsDisplayGridRowData): UseRenderLabelsParams {
    const content: LabelContent = getLabelContentFromRowData(rowData);
    const labels: ReadonlyArray<GraphLabel> = rowData.labels ? rowData.labels : [];
    return getLabelParamsForGrids({ category, labels, urls, source: 'datasource-fields', content });
  }
  return createLabelColumnFormatter(labelCategoryToString(category), { overrides: {}, getPropsFromRowData }) as
    TypedColumn<DatasourceFieldsDisplayGridRowData, FieldsValidSortFields>;
}
